import React, { useState, useEffect } from 'react';
import { FiDatabase, FiServer, FiHardDrive, FiLayers, FiRefreshCw, FiAlertCircle, FiTable, FiPlus, FiTerminal, FiSettings, FiX, FiEdit2, FiTrash2, FiEye, FiCode, FiDownload, FiKey, FiLink } from 'react-icons/fi';
import axiosConfig from '../../api/axiosConfig';
import { AddColumnModal, EditColumnModal, AddIndexModal, AddForeignKeyModal } from './DatabaseManagementStructureEdit';

const DatabaseManagement = () => {
  const [loading, setLoading] = useState(true);
  const [dbInfo, setDbInfo] = useState(null);
  const [tables, setTables] = useState([]);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [currentView, setCurrentView] = useState('main'); // main, table-view, table-structure, create-table, query, backup
  const [selectedTable, setSelectedTable] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [tableColumns, setTableColumns] = useState([]);
  const [tableIndexes, setTableIndexes] = useState([]);
  const [tableForeignKeys, setTableForeignKeys] = useState([]);
  const [tablePrimaryKey, setTablePrimaryKey] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [sqlQuery, setSqlQuery] = useState('');
  const [queryResult, setQueryResult] = useState(null);
  const [showDataModal, setShowDataModal] = useState(false);
  const [dataModalMode, setDataModalMode] = useState('create'); // create, edit
  const [editingRowId, setEditingRowId] = useState(null);
  const [rowFormData, setRowFormData] = useState({});
  
  // Modal states for structure editing
  const [showAddColumnModal, setShowAddColumnModal] = useState(false);
  const [showEditColumnModal, setShowEditColumnModal] = useState(false);
  const [showAddIndexModal, setShowAddIndexModal] = useState(false);
  const [showAddForeignKeyModal, setShowAddForeignKeyModal] = useState(false);
  const [editingColumn, setEditingColumn] = useState(null);
  
  // Form states for structure editing
  const [newColumn, setNewColumn] = useState({
    columnName: '',
    dataType: 'VARCHAR',
    length: '',
    isNullable: true,
    defaultValue: '',
    after: '',
    autoIncrement: false
  });
  
  const [editColumn, setEditColumn] = useState({
    newName: '',
    dataType: 'VARCHAR',
    length: '',
    isNullable: true,
    defaultValue: '',
    autoIncrement: false
  });
  
  const [newIndex, setNewIndex] = useState({
    indexName: '',
    columns: [],
    indexType: 'INDEX'
  });
  
  const [newForeignKey, setNewForeignKey] = useState({
    constraintName: '',
    columnName: '',
    referencedTable: '',
    referencedColumn: '',
    onDelete: 'RESTRICT',
    onUpdate: 'RESTRICT'
  });
  const [newTableForm, setNewTableForm] = useState({
    tableName: '',
    columns: [{ name: 'id', type: 'INT', length: '', notNull: true, autoIncrement: true, primary: true }]
  });
  const [backups, setBackups] = useState([]);
  const [backupLoading, setBackupLoading] = useState(false);

  // Role kontrolü
  const userRole = localStorage.getItem('adminRole');
  
  useEffect(() => {
    // Süper admin kontrolü
    if (userRole !== 'superadmin') {
      setError('Bu sayfaya erişim yetkiniz yok. Sadece süper adminler erişebilir.');
      setLoading(false);
      return;
    }
    
    fetchDatabaseInfo();
    fetchTables();
  }, [userRole]);

  const fetchDatabaseInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axiosConfig.get('/admin/database/info', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setDbInfo(response.data);
    } catch (error) {
      console.error('Database info error:', error);
      setError('Veritabanı bilgileri alınamadı');
    }
  };

  const fetchTables = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axiosConfig.get('/admin/database/tables', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setTables(response.data);
    } catch (error) {
      console.error('Tables error:', error);
      setError('Tablolar alınamadı');
    } finally {
      setLoading(false);
    }
  };

  const fetchTableData = async (tableName, pageNumber = page, searchValue = searchTerm) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        page: pageNumber,
        limit: 50,
        ...(searchValue && { search: searchValue })
      });
      const response = await axiosConfig.get(`/admin/database/tables/${tableName}/data?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setTableData(response.data.data);
      setTotalPages(response.data.pagination.totalPages);
    } catch (error) {
      console.error('Table data error:', error);
      setError('Tablo verileri alınamadı');
    } finally {
      setLoading(false);
    }
  };

  const fetchTableStructure = async (tableName) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axiosConfig.get(`/admin/database/tables/${tableName}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const columns = response.data.columns || [];
      setTableColumns(columns);
      setTableIndexes(response.data.indexes || []);
      setTableForeignKeys(response.data.foreignKeys || []);
      const primaryKeyColumn = columns.find((column) => column.column_key === 'PRI')?.column_name || null;
      setTablePrimaryKey(primaryKeyColumn);
      return response.data;
    } catch (error) {
      console.error('Table structure error:', error);
      setError('Tablo yapısı alınamadı');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchDatabaseInfo();
    await fetchTables();
    setRefreshing(false);
  };

  const handleViewTable = async (tableName) => {
    setSelectedTable(tableName);
    setCurrentView('table-view');
    setShowDataModal(false);
    setEditingRowId(null);
    setPage(1);
    setSearchTerm('');
    await fetchTableStructure(tableName);
    await fetchTableData(tableName, 1, '');
  };

  const handleViewStructure = async (tableName) => {
    setSelectedTable(tableName);
    setCurrentView('table-structure');
    setShowDataModal(false);
    await fetchTableStructure(tableName);
  };

  const buildInitialRowData = (columns, row = {}, mode = 'create') => {
    const formData = {};

    columns.forEach((column) => {
      const columnName = column.column_name;
      const isAutoIncrement = (column.extra || '').toLowerCase().includes('auto_increment');

      if (mode === 'create' && isAutoIncrement) {
        return;
      }

      const value = row[columnName];
      formData[columnName] = value === null || value === undefined ? '' : String(value);
    });

    return formData;
  };

  const prepareRowPayload = () => {
    const numericTypes = ['int', 'bigint', 'smallint', 'mediumint', 'tinyint', 'decimal', 'float', 'double'];
    const payload = {};

    Object.entries(rowFormData).forEach(([columnName, rawValue]) => {
      const column = tableColumns.find((col) => col.column_name === columnName);
      const dataType = (column?.data_type || '').toLowerCase();
      const value = typeof rawValue === 'string' ? rawValue.trim() : rawValue;

      if (dataModalMode === 'edit' && columnName === tablePrimaryKey) {
        return;
      }

      if (value === '') {
        payload[columnName] = column?.is_nullable === 'YES' ? null : '';
        return;
      }

      if (numericTypes.includes(dataType)) {
        const numericValue = Number(value);
        payload[columnName] = Number.isNaN(numericValue) ? value : numericValue;
        return;
      }

      if (dataType === 'boolean' || dataType === 'bool') {
        const normalized = String(value).toLowerCase();
        payload[columnName] = ['1', 'true', 'yes', 'evet'].includes(normalized) ? 1 : 0;
        return;
      }

      payload[columnName] = value;
    });

    return payload;
  };

  const openCreateDataModal = () => {
    setDataModalMode('create');
    setEditingRowId(null);
    setRowFormData(buildInitialRowData(tableColumns, {}, 'create'));
    setShowDataModal(true);
  };

  const openEditDataModal = (row) => {
    const rowId = tablePrimaryKey ? row?.[tablePrimaryKey] : (row?.id ?? Object.values(row || {})[0]);

    if (rowId === undefined || rowId === null) {
      setError('Kaydin anahtari bulunamadi, duzenleme yapilamiyor');
      return;
    }

    setDataModalMode('edit');
    setEditingRowId(rowId);
    setRowFormData(buildInitialRowData(tableColumns, row, 'edit'));
    setShowDataModal(true);
  };

  const handleSaveData = async () => {
    try {
      const token = localStorage.getItem('token');
      const payload = prepareRowPayload();
      let response;

      if (dataModalMode === 'edit') {
        response = await axiosConfig.put(`/admin/database/tables/${selectedTable}/data/${editingRowId}`, payload, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      } else {
        response = await axiosConfig.post(`/admin/database/tables/${selectedTable}/data`, payload, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      }

      alert(response.data.message || 'Veri kaydedildi');
      setShowDataModal(false);
      setEditingRowId(null);
      await fetchTableData(selectedTable, page, searchTerm);
    } catch (error) {
      setError(error.response?.data?.message || 'Veri kaydedilemedi');
      alert(error.response?.data?.message || 'Veri kaydedilemedi');
    }
  };

  const handleCreateTable = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axiosConfig.post('/admin/database/tables', newTableForm, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      alert(response.data.message);
      setCurrentView('main');
      await fetchTables();
    } catch (error) {
      setError(error.response?.data?.message || 'Tablo oluşturulamadı');
    }
  };

  const handleExecuteQuery = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axiosConfig.post('/admin/database/execute', { query: sqlQuery }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setQueryResult(response.data);
    } catch (error) {
      setError(error.response?.data?.message || 'Sorgu çalıştırılamadı');
    }
  };

  const handleDeleteData = async (tableName, row) => {
    const targetId = tablePrimaryKey ? row?.[tablePrimaryKey] : (row?.id ?? Object.values(row || {})[0]);

    if (targetId === undefined || targetId === null) {
      setError('Kaydin anahtari bulunamadi, silme islemi yapilamiyor');
      return;
    }

    if (!window.confirm('Bu veriyi silmek istediğinize emin misiniz?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axiosConfig.delete(`/admin/database/tables/${tableName}/data/${targetId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      alert(response.data.message);
      await fetchTableData(tableName, page, searchTerm);
    } catch (error) {
      setError(error.response?.data?.message || 'Veri silinemedi');
    }
  };
  
  // Tablo yapısı değiştirme fonksiyonları
  const handleAddColumn = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axiosConfig.post(`/admin/database/tables/${selectedTable}/columns`, newColumn, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      alert(response.data.message);
      setShowAddColumnModal(false);
      setNewColumn({ columnName: '', dataType: 'VARCHAR', length: '', isNullable: true, defaultValue: '', after: '', autoIncrement: false });
      await fetchTableStructure(selectedTable);
    } catch (error) {
      setError(error.response?.data?.message || 'Kolon eklenemedi');
      alert(error.response?.data?.message || 'Kolon eklenemedi');
    }
  };

  const handleEditColumn = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axiosConfig.put(`/admin/database/tables/${selectedTable}/columns/${editingColumn}`, editColumn, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      alert(response.data.message);
      setShowEditColumnModal(false);
      setEditingColumn(null);
      await fetchTableStructure(selectedTable);
    } catch (error) {
      setError(error.response?.data?.message || 'Kolon güncellenemedi');
      alert(error.response?.data?.message || 'Kolon güncellenemedi');
    }
  };

  const handleDeleteColumn = async (columnName) => {
    if (!window.confirm(`"${columnName}" kolonunu silmek istediğinize emin misiniz?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axiosConfig.delete(`/admin/database/tables/${selectedTable}/columns/${columnName}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      alert(response.data.message);
      await fetchTableStructure(selectedTable);
    } catch (error) {
      setError(error.response?.data?.message || 'Kolon silinemedi');
      alert(error.response?.data?.message || 'Kolon silinemedi');
    }
  };

  const handleAddIndex = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axiosConfig.post(`/admin/database/tables/${selectedTable}/indexes`, newIndex, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      alert(response.data.message);
      setShowAddIndexModal(false);
      setNewIndex({ indexName: '', columns: [], indexType: 'INDEX' });
      await fetchTableStructure(selectedTable);
    } catch (error) {
      setError(error.response?.data?.message || 'İndeks eklenemedi');
      alert(error.response?.data?.message || 'İndeks eklenemedi');
    }
  };

  const handleDropIndex = async (indexName) => {
    if (!window.confirm(`"${indexName}" indeksini silmek istediginize emin misiniz?`)) return;

    try {
      const token = localStorage.getItem('token');
      const response = await axiosConfig.delete(`/admin/database/tables/${selectedTable}/indexes/${indexName}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      alert(response.data.message);
      await fetchTableStructure(selectedTable);
    } catch (error) {
      setError(error.response?.data?.message || 'Indeks silinemedi');
      alert(error.response?.data?.message || 'Indeks silinemedi');
    }
  };

  const handleAddForeignKey = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axiosConfig.post(`/admin/database/tables/${selectedTable}/foreign-keys`, newForeignKey, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      alert(response.data.message);
      setShowAddForeignKeyModal(false);
      setNewForeignKey({ constraintName: '', columnName: '', referencedTable: '', referencedColumn: '', onDelete: 'RESTRICT', onUpdate: 'RESTRICT' });
      await fetchTableStructure(selectedTable);
    } catch (error) {
      setError(error.response?.data?.message || 'Foreign key eklenemedi');
      alert(error.response?.data?.message || 'Foreign key eklenemedi');
    }
  };

  const handleDropForeignKey = async (constraintName) => {
    if (!window.confirm(`"${constraintName}" foreign key kaydini silmek istediginize emin misiniz?`)) return;

    try {
      const token = localStorage.getItem('token');
      const response = await axiosConfig.delete(`/admin/database/tables/${selectedTable}/foreign-keys/${constraintName}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      alert(response.data.message);
      await fetchTableStructure(selectedTable);
    } catch (error) {
      setError(error.response?.data?.message || 'Foreign key silinemedi');
      alert(error.response?.data?.message || 'Foreign key silinemedi');
    }
  };

  const handleDropTable = async (tableName) => {
    if (!window.confirm(`"${tableName}" tablosunu silmek istediğinize emin misiniz? Bu işlem geri alınamaz!`)) return;
    
    // İkinci onay
    if (!window.confirm(`DİKKAT! "${tableName}" tablosu ve tüm verileri kalıcı olarak silinecek. Devam etmek istiyor musunuz?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axiosConfig.delete(`/admin/database/tables/${tableName}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      alert(response.data.message);
      await fetchTables();
    } catch (error) {
      setError(error.response?.data?.message || 'Tablo silinemedi');
      alert(error.response?.data?.message || 'Tablo silinemedi');
    }
  };

  const addColumn = () => {
    setNewTableForm({
      ...newTableForm,
      columns: [...newTableForm.columns, { name: '', type: 'VARCHAR', length: '255', notNull: false, autoIncrement: false, primary: false }]
    });
  };

  const removeColumn = (index) => {
    const newColumns = newTableForm.columns.filter((_, i) => i !== index);
    setNewTableForm({ ...newTableForm, columns: newColumns });
  };

  const updateColumn = (index, field, value) => {
    const newColumns = [...newTableForm.columns];
    newColumns[index][field] = value;
    
    // AUTO_INCREMENT seçildiğinde otomatik olarak NOT NULL ve PRIMARY KEY yap
    if (field === 'autoIncrement' && value === true) {
      newColumns[index].notNull = true;
      // Eğer başka PRIMARY KEY yoksa bu kolonu PRIMARY yap
      const hasPrimary = newColumns.some((col, i) => i !== index && col.primary);
      if (!hasPrimary) {
        newColumns[index].primary = true;
      }
    }
    
    // PRIMARY KEY kaldırıldığında AUTO_INCREMENT'i de kaldır
    if (field === 'primary' && value === false && newColumns[index].autoIncrement) {
      newColumns[index].autoIncrement = false;
    }
    
    setNewTableForm({ ...newTableForm, columns: newColumns });
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '0 KB';
    const k = 1024;
    const sizes = ['KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes * k) / Math.log(k));
    return parseFloat((bytes * Math.pow(k, i - 1)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const fetchBackups = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axiosConfig.get('/admin/database/backups', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setBackups(response.data);
    } catch (error) {
      console.error('Backups error:', error);
      setError('Yedekler alınamadı');
    } finally {
      setLoading(false);
    }
  };

  const createBackup = async () => {
    try {
      setBackupLoading(true);
      const token = localStorage.getItem('token');
      const response = await axiosConfig.post('/admin/database/backup', {}, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      alert(response.data.message);
      await fetchBackups();
    } catch (error) {
      console.error('Backup error:', error);
      setError(error.response?.data?.message || 'Yedekleme oluşturulamadı');
    } finally {
      setBackupLoading(false);
    }
  };

  const downloadBackup = async (backupId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axiosConfig.get(`/admin/database/backup/${backupId}/download`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `backup-${backupId}.sql`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Download error:', error);
      setError('Yedek indirilemedi');
    }
  };

  const restoreBackup = async (backupId) => {
    if (!window.confirm('Bu yedeği geri yüklemek istediğinize emin misiniz? Mevcut veriler kaybolabilir!')) return;
    if (!window.confirm('DİKKAT! Bu işlem geri alınamaz. Devam etmek istiyor musunuz?')) return;
    
    try {
      setBackupLoading(true);
      const token = localStorage.getItem('token');
      const response = await axiosConfig.post(`/admin/database/backup/${backupId}/restore`, {}, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      alert(response.data.message);
      await fetchDatabaseInfo();
      await fetchTables();
    } catch (error) {
      console.error('Restore error:', error);
      setError(error.response?.data?.message || 'Yedek geri yüklenemedi');
    } finally {
      setBackupLoading(false);
    }
  };

  const deleteBackup = async (backupId) => {
    if (!window.confirm('Bu yedeği silmek istediğinize emin misiniz?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axiosConfig.delete(`/admin/database/backup/${backupId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      alert(response.data.message);
      await fetchBackups();
    } catch (error) {
      console.error('Delete backup error:', error);
      setError(error.response?.data?.message || 'Yedek silinemedi');
    }
  };

  if (loading && currentView === 'main') {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  // Yetki kontrolü
  if (userRole !== 'superadmin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
          <div className="text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FiAlertCircle className="text-red-500 text-4xl" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Erişim Engellendi</h2>
            <p className="text-gray-600 mb-6">Bu sayfaya sadece süper adminler erişebilir.</p>
            <button
              onClick={() => window.history.back()}
              className="bg-red-500 text-white px-6 py-2 rounded-lg hover:bg-red-600 transition-colors"
            >
              Geri Dön
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Main View
  if (currentView === 'main') {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl p-8 text-white shadow-xl">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2 flex items-center">
                <FiDatabase className="mr-3" />
                Veritabanı Yönetimi
              </h1>
              <p className="text-purple-100">
                Veritabanı tablolarını ve verilerini yönetin
              </p>
            </div>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className={`bg-white/20 backdrop-blur-md px-6 py-3 rounded-xl font-medium hover:bg-white/30 transition-all duration-300 flex items-center space-x-2 ${
                refreshing ? 'animate-spin' : ''
              }`}
            >
              <FiRefreshCw />
              <span>Yenile</span>
            </button>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center justify-between">
            <div className="flex items-center">
              <FiAlertCircle className="text-red-500 mr-3" />
              <span className="text-red-700">{error}</span>
            </div>
            <button
              onClick={() => setError(null)}
              className="text-red-500 hover:text-red-700"
            >
              <FiX />
            </button>
          </div>
        )}

        {/* Database Info Cards */}
        {dbInfo && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center">
                  <FiServer className="text-white text-xl" />
                </div>
                <span className="text-sm text-gray-500">MySQL</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-800">{dbInfo.database}</h3>
              <p className="text-sm text-gray-600 mt-1">Veritabanı</p>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                  <FiLayers className="text-white text-xl" />
                </div>
                <span className="text-sm text-gray-500">v{dbInfo.version?.split('-')[0]}</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-800">{dbInfo.tableCount}</h3>
              <p className="text-sm text-gray-600 mt-1">Toplam Tablo</p>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center">
                  <FiHardDrive className="text-white text-xl" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-800">{dbInfo.sizeInMB} MB</h3>
              <p className="text-sm text-gray-600 mt-1">Veritabanı Boyutu</p>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center">
                  <FiSettings className="text-white text-xl" />
                </div>
              </div>
              <h3 className="text-lg font-semibold text-gray-800">{dbInfo.connection.host}</h3>
              <p className="text-sm text-gray-600 mt-1">Host: {dbInfo.connection.port}</p>
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={() => setCurrentView('create-table')}
            className="bg-white border-2 border-purple-200 rounded-xl p-6 hover:border-purple-400 hover:shadow-lg transition-all group"
          >
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center group-hover:bg-purple-200 transition-colors">
                <FiPlus className="text-purple-600 text-xl" />
              </div>
              <div className="text-left">
                <h3 className="font-semibold text-gray-800">Yeni Tablo</h3>
                <p className="text-sm text-gray-600">Yeni tablo oluştur</p>
              </div>
            </div>
          </button>

          <button
            onClick={() => setCurrentView('query')}
            className="bg-white border-2 border-pink-200 rounded-xl p-6 hover:border-pink-400 hover:shadow-lg transition-all group"
          >
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-pink-100 rounded-xl flex items-center justify-center group-hover:bg-pink-200 transition-colors">
                <FiTerminal className="text-pink-600 text-xl" />
              </div>
              <div className="text-left">
                <h3 className="font-semibold text-gray-800">SQL Sorgusu</h3>
                <p className="text-sm text-gray-600">SQL komutları çalıştır</p>
              </div>
            </div>
          </button>

          <button
            onClick={() => {
              setCurrentView('backup');
              fetchBackups();
            }}
            className="bg-white border-2 border-green-200 rounded-xl p-6 hover:border-green-400 hover:shadow-lg transition-all group"
          >
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center group-hover:bg-green-200 transition-colors">
                <FiDatabase className="text-green-600 text-xl" />
              </div>
              <div className="text-left">
                <h3 className="font-semibold text-gray-800">Yedekleme</h3>
                <p className="text-sm text-gray-600">Veritabanı yedekle</p>
              </div>
            </div>
          </button>
        </div>

        {/* Tables List */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-800 flex items-center">
              <FiTable className="mr-2" />
              Veritabanı Tabloları ({tables.length})
            </h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Tablo Adı
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Kayıt Sayısı
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Veri Boyutu
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Index Boyutu
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Oluşturulma
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    İşlemler
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {tables.map((table) => (
                  <tr key={table.table_name} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <FiTable className="text-gray-400 mr-3" />
                        <span className="font-medium text-gray-900">{table.table_name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {table.row_count?.toLocaleString('tr-TR') || '0'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {formatBytes(table.data_size_kb)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {formatBytes(table.index_size_kb)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {formatDate(table.created_at)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleViewTable(table.table_name)}
                          className="text-blue-600 hover:text-blue-900 transition-colors"
                          title="Görüntüle"
                        >
                          <FiEye className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleViewStructure(table.table_name)}
                          className="text-purple-600 hover:text-purple-900 transition-colors"
                          title="Yapı"
                        >
                          <FiCode className="w-5 h-5" />
                        </button>
                        {/* Kritik tabloları silme butonunu gizle */}
                        {!['admins', 'migrations'].includes(table.table_name) && (
                          <button
                            onClick={() => handleDropTable(table.table_name)}
                            className="text-red-600 hover:text-red-900 transition-colors"
                            title="Tabloyu Sil"
                          >
                            <FiTrash2 className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  // Table View
  if (currentView === 'table-view') {
    const tableViewColumns = tableData.length > 0
      ? Object.keys(tableData[0])
      : tableColumns.map((column) => column.column_name);

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-cyan-600 rounded-2xl p-6 text-white shadow-xl">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold mb-1 flex items-center">
                <FiTable className="mr-3" />
                {selectedTable} Tablosu
              </h1>
              <p className="text-blue-100">Tablo verilerini görüntüle ve yönet</p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={openCreateDataModal}
                className="bg-white/20 backdrop-blur-md px-4 py-2 rounded-lg hover:bg-white/30 transition-all flex items-center"
              >
                <FiPlus className="w-5 h-5 mr-2" />
                Yeni Veri
              </button>
              <button
                onClick={() => setCurrentView('main')}
                className="bg-white/20 backdrop-blur-md px-4 py-2 rounded-lg hover:bg-white/30 transition-all"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="bg-white rounded-xl shadow-lg p-4">
          <input
            type="text"
            placeholder="Tabloda ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setPage(1);
                fetchTableData(selectedTable, 1, searchTerm);
              }
            }}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Table Data */}
        <div className="bg-white rounded-xl shadow-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {tableViewColumns.map((column) => (
                    <th key={column} className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase">
                      {column}
                    </th>
                  ))}
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase">İşlemler</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {tableData.length === 0 ? (
                  <tr>
                    <td colSpan={tableViewColumns.length + 1} className="px-6 py-10 text-center text-sm text-gray-500">
                      Bu tabloda goruntulenecek veri yok.
                    </td>
                  </tr>
                ) : (
                  tableData.map((row, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      {tableViewColumns.map((column) => (
                        <td key={`${index}-${column}`} className="px-6 py-4 text-sm text-gray-900">
                          {row?.[column] === null || row?.[column] === undefined || row?.[column] === ''
                            ? '-'
                            : String(row[column])}
                        </td>
                      ))}
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-2">
                          <button className="text-blue-600 hover:text-blue-900" onClick={() => openEditDataModal(row)}>
                            <FiEdit2 className="w-4 h-4" />
                          </button>
                          <button className="text-red-600 hover:text-red-900" onClick={() => handleDeleteData(selectedTable, row)}>
                            <FiTrash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
            <span className="text-sm text-gray-700">
              Sayfa {page} / {totalPages}
            </span>
            <div className="flex space-x-2">
              <button
                onClick={() => {
                  const newPage = Math.max(1, page - 1);
                  setPage(newPage);
                  fetchTableData(selectedTable, newPage, searchTerm);
                }}
                disabled={page === 1}
                className="px-3 py-1 border rounded-lg disabled:opacity-50"
              >
                Önceki
              </button>
              <button
                onClick={() => {
                  const newPage = Math.min(totalPages, page + 1);
                  setPage(newPage);
                  fetchTableData(selectedTable, newPage, searchTerm);
                }}
                disabled={page === totalPages}
                className="px-3 py-1 border rounded-lg disabled:opacity-50"
              >
                Sonraki
              </button>
            </div>
          </div>
        </div>

        {showDataModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-800">
                  {dataModalMode === 'edit' ? 'Veri Duzenle' : 'Yeni Veri Ekle'}
                </h3>
                <button
                  onClick={() => {
                    setShowDataModal(false);
                    setEditingRowId(null);
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <FiX className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 max-h-[65vh] overflow-y-auto space-y-4">
                {tableColumns
                  .filter((column) => !(dataModalMode === 'create' && (column.extra || '').toLowerCase().includes('auto_increment')))
                  .map((column) => {
                    const isDisabled = dataModalMode === 'edit' && column.column_name === tablePrimaryKey;

                    return (
                      <div key={column.column_name}>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {column.column_name}
                          {column.column_name === tablePrimaryKey && (
                            <span className="text-xs text-gray-500 ml-2">(Primary Key)</span>
                          )}
                        </label>
                        <input
                          type="text"
                          value={rowFormData[column.column_name] ?? ''}
                          onChange={(e) => setRowFormData((prev) => ({ ...prev, [column.column_name]: e.target.value }))}
                          disabled={isDisabled}
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                            isDisabled ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : 'border-gray-300'
                          }`}
                        />
                      </div>
                    );
                  })}
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowDataModal(false);
                    setEditingRowId(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Iptal
                </button>
                <button
                  onClick={handleSaveData}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Kaydet
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Table Structure View
  if (currentView === 'table-structure') {
    const groupedIndexes = Object.values(
      tableIndexes.reduce((acc, indexRow) => {
        const keyName = indexRow.Key_name;
        if (!acc[keyName]) {
          acc[keyName] = {
            keyName,
            indexType: indexRow.Index_type,
            isUnique: indexRow.Non_unique === 0,
            columns: []
          };
        }
        acc[keyName].columns.push({
          seq: indexRow.Seq_in_index,
          column: indexRow.Column_name
        });
        return acc;
      }, {})
    ).map((indexGroup) => ({
      ...indexGroup,
      columns: indexGroup.columns
        .sort((a, b) => a.seq - b.seq)
        .map((item) => item.column)
    }));

    const groupedForeignKeys = Object.values(
      tableForeignKeys.reduce((acc, foreignKeyRow) => {
        const constraintName = foreignKeyRow.constraint_name;
        if (!acc[constraintName]) {
          acc[constraintName] = {
            constraintName,
            columns: [],
            referencedTable: foreignKeyRow.referenced_table,
            referencedColumns: []
          };
        }
        acc[constraintName].columns.push(foreignKeyRow.column_name);
        acc[constraintName].referencedColumns.push(foreignKeyRow.referenced_column);
        return acc;
      }, {})
    );

    return (
      <>
        <div className="space-y-6">
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl p-6 text-white shadow-xl">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold mb-1 flex items-center">
                  <FiCode className="mr-3" />
                  {selectedTable} Tablo Yapısı
                </h1>
                <p className="text-purple-100">Tablo kolonları ve özelliklerini düzenleyin</p>
              </div>
              <button
                onClick={() => setCurrentView('main')}
                className="bg-white/20 backdrop-blur-md px-4 py-2 rounded-lg hover:bg-white/30 transition-all"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>
          </div>
          
          {/* Quick Actions */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              onClick={() => setShowAddColumnModal(true)}
              className="bg-white border-2 border-purple-200 rounded-xl p-6 hover:border-purple-400 hover:shadow-lg transition-all group"
            >
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center group-hover:bg-purple-200 transition-colors">
                  <FiPlus className="text-purple-600 text-xl" />
                </div>
                <div className="text-left">
                  <h3 className="font-semibold text-gray-800">Kolon Ekle</h3>
                  <p className="text-sm text-gray-600">Yeni kolon ekle</p>
                </div>
              </div>
            </button>

            <button
              onClick={() => setShowAddIndexModal(true)}
              className="bg-white border-2 border-blue-200 rounded-xl p-6 hover:border-blue-400 hover:shadow-lg transition-all group"
            >
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                  <FiKey className="text-blue-600 text-xl" />
                </div>
                <div className="text-left">
                  <h3 className="font-semibold text-gray-800">İndeks Ekle</h3>
                  <p className="text-sm text-gray-600">Yeni indeks oluştur</p>
                </div>
              </div>
            </button>

            <button
              onClick={() => setShowAddForeignKeyModal(true)}
              className="bg-white border-2 border-green-200 rounded-xl p-6 hover:border-green-400 hover:shadow-lg transition-all group"
            >
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center group-hover:bg-green-200 transition-colors">
                  <FiLink className="text-green-600 text-xl" />
                </div>
                <div className="text-left">
                  <h3 className="font-semibold text-gray-800">Foreign Key</h3>
                  <p className="text-sm text-gray-600">İlişki tanımla</p>
                </div>
              </div>
            </button>
          </div>

          {/* Columns */}
          <div className="bg-white rounded-xl shadow-xl overflow-hidden">
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-800">Kolonlar</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase">Kolon Adı</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase">Veri Tipi</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase">Uzunluk</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase">Null</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase">Key</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase">Extra</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase">İşlemler</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {tableColumns.map((column) => (
                    <tr key={column.column_name} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">{column.column_name}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">{column.data_type}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">{column.max_length || '-'}</td>
                      <td className="px-6 py-4 text-sm">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          column.is_nullable === 'NO' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                        }`}>
                          {column.is_nullable}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm">
                        {column.column_key && (
                          <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                            {column.column_key}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">{column.extra || '-'}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => {
                              setEditingColumn(column.column_name);
                              setEditColumn({
                                newName: column.column_name,
                                dataType: column.data_type,
                                length: column.max_length || '',
                                isNullable: column.is_nullable === 'YES',
                                defaultValue: column.default_value || '',
                                  autoIncrement: column.extra?.toLowerCase().includes('auto_increment') || false
                            });
                              setShowEditColumnModal(true);
                            }}
                            className="text-blue-600 hover:text-blue-900 transition-colors"
                            title="Düzenle"
                          >
                            <FiEdit2 className="w-5 h-5" />
                          </button>
                          {column.column_key !== 'PRI' && (
                            <button
                              onClick={() => handleDeleteColumn(column.column_name)}
                              className="text-red-600 hover:text-red-900 transition-colors"
                              title="Sil"
                            >
                              <FiTrash2 className="w-5 h-5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Indexes */}
          <div className="bg-white rounded-xl shadow-xl overflow-hidden">
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-800">Indeksler</h2>
            </div>

            {groupedIndexes.length === 0 ? (
              <div className="px-6 py-8 text-sm text-gray-500">Bu tabloda indeks bulunmuyor.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase">Indeks Adi</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase">Tip</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase">Kolonlar</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase">Islemler</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {groupedIndexes.map((indexItem) => (
                      <tr key={indexItem.keyName} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">{indexItem.keyName}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          <span className={`px-2 py-1 text-xs rounded-full ${indexItem.isUnique ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-700'}`}>
                            {indexItem.indexType}{indexItem.isUnique ? ' / UNIQUE' : ''}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">{indexItem.columns.join(', ')}</td>
                        <td className="px-6 py-4">
                          {indexItem.keyName !== 'PRIMARY' && (
                            <button
                              onClick={() => handleDropIndex(indexItem.keyName)}
                              className="text-red-600 hover:text-red-900 transition-colors"
                              title="Indeks Sil"
                            >
                              <FiTrash2 className="w-5 h-5" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Foreign Keys */}
          <div className="bg-white rounded-xl shadow-xl overflow-hidden">
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-800">Foreign Keys</h2>
            </div>

            {groupedForeignKeys.length === 0 ? (
              <div className="px-6 py-8 text-sm text-gray-500">Bu tabloda foreign key bulunmuyor.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase">Constraint</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase">Kolon</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase">Referans</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase">Islemler</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {groupedForeignKeys.map((foreignKey) => (
                      <tr key={foreignKey.constraintName} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">{foreignKey.constraintName}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">{foreignKey.columns.join(', ')}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {foreignKey.referencedTable}.{foreignKey.referencedColumns.join(', ')}
                        </td>
                        <td className="px-6 py-4">
                          <button
                            onClick={() => handleDropForeignKey(foreignKey.constraintName)}
                            className="text-red-600 hover:text-red-900 transition-colors"
                            title="Foreign Key Sil"
                          >
                            <FiTrash2 className="w-5 h-5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Modals */}
        <AddColumnModal
          show={showAddColumnModal}
          onClose={() => setShowAddColumnModal(false)}
          onAdd={handleAddColumn}
          tableColumns={tableColumns}
          newColumn={newColumn}
          setNewColumn={setNewColumn}
        />

        <EditColumnModal
          show={showEditColumnModal}
          onClose={() => {
            setShowEditColumnModal(false);
            setEditingColumn(null);
          }}
          onEdit={handleEditColumn}
          editingColumn={editingColumn}
          editColumn={editColumn}
          setEditColumn={setEditColumn}
        />

        <AddIndexModal
          show={showAddIndexModal}
          onClose={() => setShowAddIndexModal(false)}
          onAdd={handleAddIndex}
          tableColumns={tableColumns}
          newIndex={newIndex}
          setNewIndex={setNewIndex}
        />

        <AddForeignKeyModal
          show={showAddForeignKeyModal}
          onClose={() => setShowAddForeignKeyModal(false)}
          onAdd={handleAddForeignKey}
          tableColumns={tableColumns}
          tables={tables}
          newForeignKey={newForeignKey}
          setNewForeignKey={setNewForeignKey}
        />
      </>
    );
  }

  // Create Table View
  if (currentView === 'create-table') {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 rounded-2xl p-6 text-white shadow-xl">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold mb-1 flex items-center">
                <FiPlus className="mr-3" />
                Yeni Tablo Oluştur
              </h1>
              <p className="text-green-100">Veritabanına yeni tablo ekle</p>
            </div>
            <button
              onClick={() => setCurrentView('main')}
              className="bg-white/20 backdrop-blur-md px-4 py-2 rounded-lg hover:bg-white/30 transition-all"
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Form */}
        <div className="bg-white rounded-xl shadow-xl p-6">
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Tablo Adı</label>
            <input
              type="text"
              value={newTableForm.tableName}
              onChange={(e) => setNewTableForm({ ...newTableForm, tableName: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              placeholder="örn: users"
            />
          </div>

          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Kolonlar</h3>
              <button
                onClick={addColumn}
                className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors flex items-center"
              >
                <FiPlus className="mr-2" />
                Kolon Ekle
              </button>
            </div>

            <div className="space-y-4">
              {newTableForm.columns.map((column, index) => (
                <div key={index} className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
                  <input
                    type="text"
                    value={column.name}
                    onChange={(e) => updateColumn(index, 'name', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="Kolon adı"
                  />
                  <select
                    value={column.type}
                    onChange={(e) => updateColumn(index, 'type', e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="INT">INT</option>
                    <option value="BIGINT">BIGINT</option>
                    <option value="VARCHAR">VARCHAR</option>
                    <option value="TEXT">TEXT</option>
                    <option value="DATETIME">DATETIME</option>
                    <option value="DECIMAL">DECIMAL</option>
                    <option value="FLOAT">FLOAT</option>
                    <option value="DOUBLE">DOUBLE</option>
                    <option value="BOOLEAN">BOOLEAN</option>
                    <option value="DATE">DATE</option>
                    <option value="TIME">TIME</option>
                    <option value="TIMESTAMP">TIMESTAMP</option>
                    <option value="JSON">JSON</option>
                  </select>
                  <input
                    type="text"
                    value={column.length}
                    onChange={(e) => updateColumn(index, 'length', e.target.value)}
                    className="w-20 px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="Uzunluk"
                  />
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={column.notNull}
                      onChange={(e) => updateColumn(index, 'notNull', e.target.checked)}
                      className="mr-2"
                    />
                    NOT NULL
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={column.autoIncrement}
                      onChange={(e) => updateColumn(index, 'autoIncrement', e.target.checked)}
                      className="mr-2"
                      disabled={!['INT', 'BIGINT'].includes(column.type)}
                    />
                    <span className={['INT', 'BIGINT'].includes(column.type) ? '' : 'text-gray-400'}>
                      AUTO
                    </span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={column.primary}
                      onChange={(e) => updateColumn(index, 'primary', e.target.checked)}
                      className="mr-2"
                    />
                    PRIMARY
                  </label>
                  <button
                    onClick={() => removeColumn(index)}
                    className="text-red-600 hover:text-red-900"
                  >
                    <FiTrash2 />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            <button
              onClick={() => setCurrentView('main')}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              İptal
            </button>
            <button
              onClick={handleCreateTable}
              className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
            >
              Tabloyu Oluştur
            </button>
          </div>
        </div>
      </div>
    );
  }

  // SQL Query View
  if (currentView === 'query') {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-6 text-white shadow-xl">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold mb-1 flex items-center">
                <FiTerminal className="mr-3" />
                SQL Sorgu Editörü
              </h1>
              <p className="text-indigo-100">SQL komutlarını çalıştır</p>
            </div>
            <button
              onClick={() => setCurrentView('main')}
              className="bg-white/20 backdrop-blur-md px-4 py-2 rounded-lg hover:bg-white/30 transition-all"
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Warning */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 flex items-start">
          <FiAlertCircle className="text-yellow-600 mr-3 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-semibold text-yellow-800">Dikkat!</h3>
            <p className="text-yellow-700 text-sm mt-1">
              SQL sorguları doğrudan veritabanında çalıştırılır. DROP, DELETE gibi tehlikeli komutları dikkatli kullanın.
            </p>
          </div>
        </div>

        {/* Query Editor */}
        <div className="bg-white rounded-xl shadow-xl p-6">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">SQL Sorgusu</label>
            <textarea
              value={sqlQuery}
              onChange={(e) => setSqlQuery(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 font-mono text-sm"
              rows="10"
              placeholder="SELECT * FROM table_name;"
            />
          </div>

          <div className="flex justify-between">
            <div className="text-sm text-gray-600">
              Desteklenen komutlar: SELECT, INSERT, UPDATE, DELETE, CREATE, ALTER
            </div>
            <button
              onClick={handleExecuteQuery}
              className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center"
            >
              <FiTerminal className="mr-2" />
              Çalıştır
            </button>
          </div>
        </div>

        {/* Query Result */}
        {queryResult && (
          <div className="bg-white rounded-xl shadow-xl p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Sonuç</h3>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-green-800">{queryResult.message}</p>
              {queryResult.affectedRows !== undefined && (
                <p className="text-green-700 text-sm mt-1">
                  Etkilenen satır sayısı: {queryResult.affectedRows}
                </p>
              )}
            </div>
            {queryResult.result && Array.isArray(queryResult.result) && (
              <div className="mt-4 overflow-x-auto">
                <table className="w-full border border-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {Object.keys(queryResult.result[0] || {}).map((key) => (
                        <th key={key} className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase border-b">
                          {key}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {queryResult.result.map((row, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        {Object.values(row).map((value, i) => (
                          <td key={i} className="px-4 py-2 text-sm text-gray-900 border-b">
                            {value?.toString() || '-'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // Backup View
  if (currentView === 'backup') {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 rounded-2xl p-6 text-white shadow-xl">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold mb-1 flex items-center">
                <FiDatabase className="mr-3" />
                Veritabanı Yedekleme
              </h1>
              <p className="text-green-100">Veritabanı yedeklerini yönet</p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={createBackup}
                disabled={backupLoading}
                className="bg-white/20 backdrop-blur-md px-4 py-2 rounded-lg hover:bg-white/30 transition-all flex items-center disabled:opacity-50"
              >
                {backupLoading ? (
                  <FiRefreshCw className="w-5 h-5 mr-2 animate-spin" />
                ) : (
                  <FiPlus className="w-5 h-5 mr-2" />
                )}
                Yeni Yedek Oluştur
              </button>
              <button
                onClick={() => setCurrentView('main')}
                className="bg-white/20 backdrop-blur-md px-4 py-2 rounded-lg hover:bg-white/30 transition-all"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center">
                <FiHardDrive className="text-white text-xl" />
              </div>
            </div>
            <h3 className="text-2xl font-bold text-gray-800">{backups.length}</h3>
            <p className="text-sm text-gray-600 mt-1">Toplam Yedek</p>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center">
                <FiDatabase className="text-white text-xl" />
              </div>
            </div>
            <h3 className="text-lg font-semibold text-gray-800">{dbInfo?.database}</h3>
            <p className="text-sm text-gray-600 mt-1">Veritabanı</p>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                <FiSettings className="text-white text-xl" />
              </div>
            </div>
            <h3 className="text-2xl font-bold text-gray-800">{dbInfo?.sizeInMB} MB</h3>
            <p className="text-sm text-gray-600 mt-1">Veritabanı Boyutu</p>
          </div>
        </div>

        {/* Backup List */}
        <div className="bg-white rounded-xl shadow-xl overflow-hidden">
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-800">Yedek Listesi</h2>
          </div>
          
          {backups.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FiDatabase className="text-gray-400 text-3xl" />
              </div>
              <h3 className="text-lg font-medium text-gray-800 mb-2">Henüz yedek yok</h3>
              <p className="text-gray-600">Yeni yedek oluştur butonuna tıklayarak ilk yedeğinizi oluşturabilirsiniz.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase">Yedek Adı</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase">Boyut</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase">Oluşturulma Tarihi</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase">Durum</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase">İşlemler</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {backups.map((backup) => (
                    <tr key={backup.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <FiDatabase className="text-gray-400 mr-3" />
                          <span className="font-medium text-gray-900">{backup.filename}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {formatBytes(backup.size)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {formatDate(backup.created_at)}
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                          {backup.status || 'Tamamlandı'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => downloadBackup(backup.id)}
                            className="text-blue-600 hover:text-blue-900 transition-colors"
                            title="İndir"
                          >
                            <FiDownload className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => restoreBackup(backup.id)}
                            disabled={backupLoading}
                            className="text-green-600 hover:text-green-900 transition-colors disabled:opacity-50"
                            title="Geri Yükle"
                          >
                            <FiRefreshCw className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => deleteBackup(backup.id)}
                            className="text-red-600 hover:text-red-900 transition-colors"
                            title="Sil"
                          >
                            <FiTrash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Instructions */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
          <h3 className="font-semibold text-blue-800 mb-3 flex items-center">
            <FiAlertCircle className="mr-2" />
            Yedekleme Hakkında
          </h3>
          <ul className="space-y-2 text-blue-700 text-sm">
            <li>• Yedekler sunucuda güvenli bir şekilde saklanır</li>
            <li>• Yedekleme işlemi veritabanı boyutuna göre zaman alabilir</li>
            <li>• Geri yükleme işlemi mevcut verilerin üzerine yazacaktır</li>
            <li>• Düzenli yedekleme yapmanız önerilir</li>
          </ul>
        </div>
      </div>
    );
  }

  return null;
};

export default DatabaseManagement;
